import React from 'react';
import { PhoneCallMessage, PhoneChatReplyOption } from '../../types';
import styles from './scenario-studio.module.css';

function SceneSelect({
  value,
  sceneIds,
  onChange,
  label = 'Scène suivante (gotoScene)',
}: {
  value?: string;
  sceneIds: string[];
  onChange: (v: string) => void;
  label?: string;
}) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <select className={styles.select} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {sceneIds.map((id) => (
          <option key={id} value={id}>{id}</option>
        ))}
      </select>
    </div>
  );
}

interface PhoneCallMessagesEditorProps {
  messages: PhoneCallMessage[];
  onChange: (messages: PhoneCallMessage[]) => void;
}

export const PhoneCallMessagesEditor: React.FC<PhoneCallMessagesEditorProps> = ({
  messages,
  onChange,
}) => {
  const update = (index: number, patch: Partial<PhoneCallMessage>) => {
    onChange(messages.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...messages];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(newIdx, 0, item!);
    onChange(next);
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Messages de l&apos;appel</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => onChange([...messages, { sender: 'contact', text: '' }])}
        >
          + Message
        </button>
      </div>
      {messages.length === 0 && (
        <p className={styles.hint}>Ajoutez les répliques dans l&apos;ordre de l&apos;appel.</p>
      )}
      {messages.map((msg, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.row}>
            <div className={styles.field} style={{ flex: '0 0 140px' }}>
              <label>Émetteur</label>
              <select
                className={styles.select}
                value={msg.sender}
                onChange={(e) =>
                  update(index, { sender: e.target.value as PhoneCallMessage['sender'] })
                }
              >
                <option value="contact">Contact</option>
                <option value="player">Joueur</option>
              </select>
            </div>
            <div className={styles.inlineActions}>
              <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
              <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
              <button
                type="button"
                onClick={() => onChange(messages.filter((_, i) => i !== index))}
                title="Supprimer"
              >
                ×
              </button>
            </div>
          </div>
          <div className={styles.field}>
            <label>Texte</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={msg.text}
              onChange={(e) => update(index, { text: e.target.value })}
              placeholder="Réplique…"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

interface PhoneReplyOptionsEditorProps {
  options: PhoneChatReplyOption[];
  sceneIds: string[];
  onChange: (options: PhoneChatReplyOption[]) => void;
  depth?: number;
  title?: string;
}

export const PhoneReplyOptionsEditor: React.FC<PhoneReplyOptionsEditorProps> = ({
  options,
  sceneIds,
  onChange,
  depth = 0,
  title,
}) => {
  const update = (index: number, patch: Partial<PhoneChatReplyOption>) => {
    onChange(options.map((o, i) => (i === index ? { ...o, ...patch } : o)));
  };

  const sectionTitle =
    title ?? (depth === 0 ? 'Réponses du joueur (après les messages)' : 'Réponses de suivi');

  return (
    <div
      className={styles.phoneSection}
      style={depth > 0 ? { marginLeft: `${Math.min(depth, 4) * 12}px` } : undefined}
    >
      <div className={styles.panelHeader}>
        <h5>{sectionTitle}</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => onChange([...options, { text: '' }])}
        >
          + Réponse
        </button>
      </div>

      {options.length === 0 && depth === 0 && (
        <p className={styles.hint}>
          Optionnel : choix proposés au joueur pendant l&apos;appel. Sinon, utilisez « Scène suivante » en bas.
        </p>
      )}

      {options.map((opt, index) => {
        const hasNested = (opt.replyOptions?.length ?? 0) > 0;
        return (
          <div key={index} className={styles.card}>
            <div className={styles.field}>
              <label>Texte du joueur (bouton + réplique joueur)</label>
              <input
                className={styles.input}
                value={opt.text}
                onChange={(e) => update(index, { text: e.target.value })}
                placeholder="Ce que dit le joueur…"
              />
            </div>
            <div className={styles.field}>
              <label>Réplique du contact (après ce choix)</label>
              <textarea
                className={styles.textarea}
                rows={2}
                value={opt.contactReply ?? ''}
                onChange={(e) =>
                  update(index, { contactReply: e.target.value || undefined })
                }
                placeholder="Réponse du contact…"
              />
            </div>

            {!hasNested && (
              <SceneSelect
                value={opt.goto}
                sceneIds={sceneIds}
                onChange={(goto) =>
                  update(index, { goto: goto || undefined })
                }
              />
            )}

            <PhoneReplyOptionsEditor
              options={opt.replyOptions ?? []}
              sceneIds={sceneIds}
              depth={depth + 1}
              title="Réponses de suivi (après la réplique du contact)"
              onChange={(replyOptions) =>
                update(index, {
                  replyOptions: replyOptions.length > 0 ? replyOptions : undefined,
                  ...(replyOptions.length > 0 ? { gotoScene: undefined } : {}),
                })
              }
            />

            <button
              type="button"
              className={styles.btnDanger}
              onClick={() => onChange(options.filter((_, i) => i !== index))}
            >
              Supprimer cette réponse
            </button>
          </div>
        );
      })}
    </div>
  );
};
